import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import {
  TableBorderRadiusTopLeftRight,
  StyledTableCell,
  pageSize,
  TablePaginationStyle,
  paginationLabel,
  NoDataContainer,
} from "../../../Common/styles/table";
import http from "../../../../utils/http";
import { useNavigate, useParams } from "react-router-dom";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import { AxiosResponse } from "axios";
import { DateTime } from "luxon";
import DownloadIcon from "@mui/icons-material/Download";
import SendConsentDrawer from "./SendConsentDrawer";
import { HeaderStyle } from "../ptpTabs.style";
import { useAppSelector } from "../../../../Redux/hooks";
import { MoreVert } from "@mui/icons-material";
import VerifyConsentModal from "./VerifyConsentModal";
import StatusBadge from "../../../Common/UI/StatusTag";

const statusMap: any = {
  in_progress: { label: "Inprogress", variant: "pending" },
  signed: { label: "Participant Signed", variant: "active" },
  not_started: { label: "Sent", variant: "sent" },
  provider_signed: { label: "Completed", variant: "sent" },
  verified: { label: "Verified", variant: "sent" },
};

type Props = {
  formType: "consent" | "sunshine";
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
};

const ConsentFormsList = ({ formType, page, setPage }: Props) => {
  const { id: studyId } = useParams();
  const { participantId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState<any>([]);
  const [dataCount, setDataCount] = useState(0);

  const [showConsentDrawer, setShowConsentDrawer] = useState<boolean>(false);
  const [toggleLoader, setToggleLoader] = useState<boolean>(false);

  const [loading, setLoading] = useState(true);
  const [downloadingRowId, setDownloadingRowId] = useState<string>("");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedConsents, setSelectedConsents] = useState<Set<string>>(
    new Set()
  );
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);

  const assignable = useAppSelector((state) => state.participantTab.assignable);
  const isLocked = useAppSelector((state) => state.participantTab.isLocked);

  const openConsentModal = () => {
    setShowConsentDrawer(true);
  };
  const closeConsentDrawer = () => {
    setShowConsentDrawer(false);
  };
  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        let url = `/admin/study/${studyId}/econsent/participants/${participantId}/econsents`;
        if (formType === "sunshine") {
          url = `/admin/study/${studyId}/sunshine/participants/${participantId}/sunshine-forms`;
        }
        url += `?page=${page + 1}&size=${pageSize}`;

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.data?.map((item: any, index: number) => ({
          id: item?.consentVersion?.id,
          name: item?.consentVersion?.consent?.name,
          version: item?.consentVersion?.version,
          consentId: item?.consentVersion?.consentId,
          consentDate: item?.signedOn
            ? DateTime.fromISO(item?.signedOn).toFormat("MMM dd, yyyy")
            : "-",
          status: item?.status,
          sentOn: item?.sentOn
            ? DateTime.fromISO(item?.sentOn).toFormat("MMM dd, yyyy")
            : "-",
        }));

        setData(newData || []);
        setDataCount(data?.meta?.paginationOptions?.totalItems ?? 0);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [page, studyId, participantId, toggleLoader, formType]);

  useEffect(() => {
    setSelectedConsents(new Set());
  }, [formType]);

  const handleDownload = async (versionId: string) => {
    try {
      setDownloadingRowId(versionId);

      let prefix = `/admin/study/${studyId}/econsent/participants/${participantId}/signed-econsents`;
      if (formType === "sunshine") {
        prefix = `/admin/study/${studyId}/sunshine/participants/${participantId}/signed-sunshine-forms`;
      }
      let res = await http.get(`${prefix}/${versionId}/download`);
      const downloadLink = res?.data?.data?.downloadUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setDownloadingRowId("");
    } catch (err) {
      setDownloadingRowId("");
      errorToastMessage(err as Error);
    }
  };

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  const resendConsent = async (versionId: string) => {
    try {
      setDownloadingRowId(versionId);
      let body: any = {
        versionId: versionId,
      };
      let prefix = `/admin/study/${studyId}/econsent`;
      if (formType === "sunshine") {
        prefix = `/admin/study/${studyId}/sunshine`;
      }
      let res = await http.post(
        `${prefix}/participants/${participantId}/resend`,
        body
      );
      toastMessage("success", res.data.message);
      setDownloadingRowId("");
    } catch (err) {
      setDownloadingRowId("");
      errorToastMessage(err as Error);
    }
  };

  const handleSignConsent = (
    consentId: string,
    versionId: string,
    status: string
  ) => {
    if (status === "signed") {
      navigate(
        `/studies/${studyId}/participants/${participantId}/consent-form/${consentId}?versionId=${versionId}`
      );
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openVerifyModal = () => {
    setAnchorEl(null);
    setShowVerifyModal(true);
  };

  const closeVerifyModal = () => {
    setShowVerifyModal(false);
    setSelectedRow(null);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedConsents((prev) => {
        const newConsentIds = data
          .filter(
            (item: any) =>
              item?.status === "signed" ||
              item?.status === "provider_signed" ||
              item?.status === "verified"
          )
          .map((item: any) => item.id);
        return new Set([...prev, ...newConsentIds]);
      });
    }
  };

  const handleSelectRow = (
    event: React.ChangeEvent<HTMLInputElement>,
    versionId: string
  ) => {
    if (event.target.checked) {
      setSelectedConsents((prev) => new Set([...prev, versionId]));
    } else {
      setSelectedConsents((prev) => {
        const newSet = new Set(prev);
        newSet.delete(versionId);
        return newSet;
      });
    }
  };

  const isRowSelected = (versionId: string) => {
    return selectedConsents.has(versionId);
  };

  const handleBulkDownload = async () => {
    if (selectedConsents.size === 0) {
      toastMessage(
        "warning",
        `Please select at least one ${
          formType === "consent" ? "consent" : "form"
        } to download`
      );
      return;
    }

    try {
      setBulkDownloadLoading(true);
      let prefix = `/admin/study/${studyId}/econsent/participants/${participantId}/signed-econsents`;
      if (formType === "sunshine") {
        prefix = `/admin/study/${studyId}/sunshine/participants/${participantId}/signed-sunshine-forms`;
      }

      const res = await http.post(
        `${prefix}/bulk-download`,
        {
          studyId,
          participantId,
          consentVersionIds: Array.from(selectedConsents),
        },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${
        formType === "consent" ? "consents" : "forms"
      }-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toastMessage(
        "success",
        `${selectedConsents.size} ${
          formType === "consent" ? "consent" : "form"
        }(s) downloaded successfully`
      );
      setSelectedConsents(new Set());
      setBulkDownloadLoading(false);
    } catch (err) {
      setBulkDownloadLoading(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <>
      <Box
        sx={{
          ...HeaderStyle,
          display: "flex",
          justifyContent: "flex-end",
          p: "16px 24px",
        }}
      >
        {assignable && (
          <Button variant="outlined" onClick={openConsentModal}>
            Send {formType === "consent" ? "Consent" : "Sunshine Form"}
          </Button>
        )}
      </Box>
      <Box sx={{ p: 2.5, overflow: "auto", height: "calc(100% - 80px)" }}>
        <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "10px" }}>
          {selectedConsents.size > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="body1" fontWeight="medium">
                {selectedConsents.size}{" "}
                {formType === "consent" ? "consent" : "form"}(s) selected
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setSelectedConsents(new Set())}
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    bulkDownloadLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={handleBulkDownload}
                  disabled={bulkDownloadLoading}
                >
                  Download Selected
                </Button>
              </Box>
            </Box>
          )}
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={TableBorderRadiusTopLeftRight}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Checkbox checked={false} onChange={handleSelectAll} />
                      <Typography fontWeight={"bold"} variant="body2">
                        {formType === "consent" ? "Consent Name" : "Form Name"}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  {/* <StyledTableCell>Provider Name</StyledTableCell> */}
                  <StyledTableCell>
                    {formType === "consent"
                      ? "Provider Consent"
                      : "Provider Signature"}
                  </StyledTableCell>
                  <StyledTableCell>Version</StyledTableCell>
                  <StyledTableCell>
                    {formType === "consent" ? "Consent Date" : "Form Date"}
                  </StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                  {assignable && <StyledTableCell align="right" />}
                </TableRow>
              </TableHead>
              {!loading && data?.length > 0 && (
                <>
                  <TableBody>
                    {data?.map((item: any) => {
                      const isSelectable =
                        item?.status === "signed" ||
                        item?.status === "provider_signed" ||
                        item?.status === "verified";
                      const isSelected = isRowSelected(item.id);

                      return (
                        <TableRow key={item?.id}>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled={!isSelectable}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleSelectRow(e, item.id)}
                              />
                              <Typography
                                variant="body1"
                                fontWeight={"regular"}
                                sx={{ flexShrink: 0 }}
                              >
                                {item?.name}
                              </Typography>
                            </Box>
                          </StyledTableCell>
                          {/* <StyledTableCell>
                        {item?.providerName || "-"}
                      </StyledTableCell> */}
                          <StyledTableCell>
                            <Typography
                              onClick={() => {
                                if (assignable)
                                  handleSignConsent(
                                    item?.consentId,
                                    item?.id,
                                    item?.status
                                  );
                              }}
                              sx={{
                                cursor:
                                  item?.status === "signed" && assignable
                                    ? "pointer"
                                    : "auto",
                                color:
                                  item?.status === "signed"
                                    ? "primary.main"
                                    : "text.primary",
                              }}
                            >
                              {item?.status === "provider_signed" ||
                              item?.status === "verified"
                                ? "Signed"
                                : item?.status === "signed" && assignable
                                ? `Sign ${
                                    formType === "consent" ? "Consent" : "Form"
                                  }`
                                : "-"}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>{item?.version}</StyledTableCell>
                          <StyledTableCell>{item?.consentDate}</StyledTableCell>
                          <StyledTableCell>
                            <StatusBadge
                              label={
                                statusMap[item?.status]?.label ?? item?.status
                              }
                              variant={statusMap[item?.status]?.variant}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            {downloadingRowId === item?.id ? (
                              <CircularProgress size={18} />
                            ) : item?.status === "signed" ||
                              item?.status === "provider_signed" ||
                              item?.status === "verified" ? (
                              <IconButton
                                onClick={() => handleDownload(item?.id)}
                              >
                                <DownloadIcon />
                              </IconButton>
                            ) : assignable ? (
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: "regular",
                                  color: "primary.main",
                                  cursor: "pointer",
                                }}
                                onClick={() => resendConsent(item?.id)}
                              >
                                Remind Again
                              </Typography>
                            ) : (
                              "-"
                            )}
                          </StyledTableCell>
                          {assignable && (
                            <StyledTableCell align="right">
                              {(item?.status === "provider_signed" ||
                                item?.status === "signed") && (
                                <IconButton
                                  onClick={(e) => handleMenuClick(e, item)}
                                >
                                  <MoreVert />
                                </IconButton>
                              )}
                            </StyledTableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  {dataCount > pageSize && (
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          sx={TablePaginationStyle}
                          count={dataCount}
                          page={page}
                          rowsPerPage={pageSize}
                          rowsPerPageOptions={[pageSize]}
                          onPageChange={handleChangePage}
                          labelDisplayedRows={paginationLabel}
                        />
                      </TableRow>
                    </TableFooter>
                  )}
                </>
              )}
            </Table>
          </Box>
          {!loading && data.length === 0 && (
            <NoDataContainer>
              <Typography variant="body1" color="gray">
                No Data
              </Typography>
            </NoDataContainer>
          )}
          {loading && (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}

          <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
            <MenuItem onClick={openVerifyModal}>
              <ListItemText>
                Verify {formType === "consent" ? "Consent" : "Form"}
              </ListItemText>
            </MenuItem>
          </Menu>

          <Drawer
            open={showConsentDrawer}
            onClose={closeConsentDrawer}
            anchor={"right"}
          >
            <SendConsentDrawer
              closeDrawer={closeConsentDrawer}
              refreshPage={refreshPage}
              formType={formType}
            />
          </Drawer>

          {showVerifyModal && (
            <VerifyConsentModal
              formType={formType}
              show={showVerifyModal}
              onClose={closeVerifyModal}
              data={selectedRow}
              refreshPage={refreshPage}
              studyId={studyId}
              type="study"
              isLocked={isLocked}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default ConsentFormsList;
